/**
 * Página de Productos Horeca - Versión Inmersiva (Interior Page Style)
 * Diseño Awwwards para páginas interiores: elegante, funcional, con animaciones sutiles
 * 
 * Según estructura.md - Subpágina Productos Horeca:
 * - Título: Productos Horeca
 * - Subtítulo: Salsas y productos pensados para hostelería, restauración, cocinas profesionales y grandes consumos
 * - NO lleva imagen/banner en el hero (a diferencia de Productos o Quiénes Somos)
 * 
 * Grid de Productos Horeca por categorías:
 *   1. Formato Biberón: Ketchup, Mostaza Dulce, Barbacoa, Alioli Receta Canaria
 *   2. Formato Garrafa: Mostaza Dulce, Barbacoa, Ketchup
 *   3. Formato Cubo: Ketchup, Mostaza Dulce, Barbacoa
 *   4. Formato Lata: Ketchup, Salsa Pizza, Tomate Frito, Concentrado de Tomate, Tomate Pelado, Tomate Triturado
 *   5. Mayonesas y Alioli en Cubo: Mayonesa, Mayonesa Ligera, Alioli Receta Canaria
 */

import React, { useEffect, useRef, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { AnimatedSection, MagneticButton } from '../components/animations';
import { horecaProductsData } from '../data/horecaProductsData';

gsap.registerPlugin(ScrollTrigger);

/**
 * Componente de tarjeta de producto Horeca con estilo Awwwards
 * @param {Object} props - Propiedades del producto
 */
const ProductCardImmersive = ({ id, name, shortName, formatDisplay, shortDescription, image, imageScale }) => {
  const displayName = shortName || name;
  const productLink = `/productos/horeca/${id}`;

  return (
    <Link to={productLink} className="group block h-full">
      <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 h-full flex flex-col">
        {/* Imagen del producto - más compacta */}
        <div className="relative aspect-[4/3] bg-intercasa-bone flex items-center justify-center overflow-hidden flex-shrink-0">
          {image ? (
            // El wrapper aplica la escala base del producto para que envases con
            // distinto volumen se vean proporcionados entre si; el hover sigue en la imagen.
            // Se escala desde la base para que todos los envases apoyen en la misma
            // linea: escalando desde el centro el envase reducido parece flotar.
            <div
              className="w-full h-full"
              style={imageScale ? { transform: `scale(${imageScale})`, transformOrigin: 'bottom center' } : undefined}
            >
              <img
                src={image}
                alt={name}
                className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500 ease-out"
              />
            </div>
          ) : (
            /* Placeholder elegante para Horeca */
            <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-intercasa-bone to-white">
              <svg 
                className="w-10 h-10 text-intercasa-dark/20" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
              </svg>
            </div>
          )}
          
          {/* Badge de formato */}
          {formatDisplay && (
            <div className="absolute top-2 right-2 bg-intercasa-dark/80 backdrop-blur-sm text-white text-[10px] font-lato font-medium px-2 py-1 rounded-full">
              {formatDisplay}
            </div>
          )}

          {/* Overlay hover */}
          <div className="absolute inset-0 bg-intercasa-dark/0 group-hover:bg-intercasa-dark/5 transition-colors duration-300" />
        </div>

        {/* Contenido - altura fija */}
        <div className="p-3 md:p-4 flex-1 flex flex-col min-h-[120px]">
          <h3 className="font-baskerville text-sm md:text-base font-bold text-intercasa-dark mb-1 group-hover:text-intercasa-red transition-colors duration-300 min-h-[40px]">
            {displayName}
          </h3>
          
          {shortDescription && (
            <p className="font-lato text-xs text-intercasa-dark/60 leading-relaxed mb-2 flex-1">
              {shortDescription}
            </p>
          )}

          {/* CTA */}
          <div className="flex items-center text-intercasa-red font-lato font-medium text-xs group-hover:translate-x-1 transition-transform duration-300">
            <span>Ver producto</span>
            <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </div>
    </Link>
  );
};

/**
 * Componente de sección de categoría
 * @param {Object} props - Propiedades de la categoría
 */
const CategorySection = ({ title, products, delay = 0 }) => {
  return (
    <section className="mb-12 md:mb-16">
      <AnimatedSection animation="fadeUp" delay={delay}>
        <div className="flex items-center mb-6 md:mb-8">
          <div className="w-8 h-px bg-intercasa-dark mr-3" />
          <h2 className="font-baskerville font-bold text-intercasa-dark text-xl md:text-2xl">
            {title}
          </h2>
        </div>
      </AnimatedSection>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
        {products.map((product, index) => (
          <AnimatedSection key={product.id} animation="fadeUp" delay={delay + 0.05 + (index * 0.03)} className="h-full">
            <ProductCardImmersive {...product} />
          </AnimatedSection>
        ))}
      </div>
    </section>
  );
};

/**
 * Página de productos Horeca con diseño inmersivo estilo Awwwards
 * @returns {JSX.Element} Página de productos horeca inmersiva
 */
const HorecaProductsPageImmersive = () => {
  const heroRef = useRef(null);
  const location = useLocation();

  // Scroll al inicio cuando se carga la página
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    
    const timer = setTimeout(() => {
      window.scrollTo(0, 0);
      if (window.__lenis) {
        window.__lenis.scrollTo(0, { immediate: true });
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [location.key]);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    
    // Animación de entrada del hero
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' }
    )
    .fromTo(hero.querySelector('.hero-subtitle'),
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' },
      '-=0.3'
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* 
        =============================================
        1. HERO CON BANNER - Imagen de fondo en patrón
        Usando banner-negro.png para productos horeca
        =============================================
      */}
      <section 
        ref={heroRef}
        className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen en patrón repetido (tile) */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />

        {/* Contenido del hero */}
        <div className="relative z-10 container mx-auto px-4 md:px-8">
          <div className="max-w-4xl">
            {/* Breadcrumb */}
            <div className="flex items-center space-x-2 text-white/60 font-lato text-sm mb-6">
              <Link to="/productos" className="hover:text-white transition-colors">Productos</Link>
              <span>/</span>
              <span className="text-intercasa-yellow">Horeca</span>
            </div>

            {/* Título según estructura.md */}
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-4">
              Productos Horeca
            </h1>

            {/* Subtítulo según estructura.md */}
            <p className="hero-subtitle font-lato text-white/90 text-lg md:text-xl leading-relaxed max-w-2xl">
              Salsas y productos pensados para hostelería, restauración, cocinas profesionales y grandes consumos.
            </p>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        2. GRID DE PRODUCTOS POR CATEGORÍAS
        Según estructura.md
        =============================================
      */}
      <section className="bg-intercasa-bone py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8">
          
          {/* Categoría 1: Formato Biberón */}
          <CategorySection 
            title="Formato Biberón" 
            products={horecaProductsData.biberon}
            delay={0}
          />

          {/* Categoría 2: Formato Garrafa */}
          <CategorySection 
            title="Formato Garrafa" 
            products={horecaProductsData.garrafa}
            delay={0.1}
          />

          {/* Categoría 3: Formato Cubo */}
          <CategorySection 
            title="Formato Cubo" 
            products={horecaProductsData.cubo}
            delay={0.2}
          />

          {/* Categoría 4: Mayonesas y Alioli en Cubo */}
          <CategorySection 
            title="Mayonesas y Alioli en Cubo" 
            products={horecaProductsData.mayonesasAlioli}
            delay={0.3}
          />

          {/* Categoría 5: Formato Lata */}
          <CategorySection 
            title="Formato Lata" 
            products={horecaProductsData.lata}
            delay={0.4}
          />

        </div>
      </section>

      {/* 
        =============================================
        3. CTA HACIA PRODUCTOS RETAIL
        =============================================
      */}
      <section className="bg-intercasa-red py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Contenido */}
            <div>
              <AnimatedSection animation="fadeUp">
                <span className="font-lato text-intercasa-yellow text-sm uppercase tracking-widest mb-4 block">
                  Formatos familiares
                </span>
                <h2 className="font-baskerville font-bold text-white text-3xl md:text-4xl lg:text-5xl mb-6 leading-tight">
                  ¿Buscas productos para{' '}
                  <span className="text-intercasa-yellow">el hogar?</span>
                </h2>
              </AnimatedSection>
              
              <AnimatedSection animation="fadeUp" delay={0.1}>
                <p className="font-lato text-white/80 text-lg leading-relaxed mb-8">
                  Descubre nuestra línea Retail, diseñada específicamente para el consumo 
                  doméstico con formatos familiares y la misma calidad Intercasa que 
                  los profesionales conocen.
                </p>
              </AnimatedSection>

              <AnimatedSection animation="fadeUp" delay={0.2}>
                <Link to="/productos/retail">
                  <MagneticButton variant="secondary" size="lg">
                    Ver productos Retail
                  </MagneticButton>
                </Link>
              </AnimatedSection>
            </div>

            {/* Visual decorativo */}
            <AnimatedSection animation="fadeUp" delay={0.2}>
              <div className="relative">
                <div className="aspect-square bg-gradient-to-br from-white/10 to-intercasa-yellow/10 rounded-2xl flex items-center justify-center overflow-hidden">
                  {/* Imagen productos retail */}
                  <img 
                    src="/images/productos/productos-retail.webp"
                    alt="Productos Retail"
                    className="w-full h-full object-cover"
                  />
                </div>
                
                {/* Badge flotante */}
                <div className="absolute -bottom-4 -left-4 bg-intercasa-yellow text-intercasa-dark px-6 py-3 rounded-xl shadow-lg">
                  <span className="font-baskerville font-bold text-2xl">+12</span>
                  <span className="font-lato text-sm ml-2">productos Retail</span>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        4. CONTACTO CTA
        =============================================
      */}
      <section className="bg-intercasa-bone py-16 md:py-20">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection animation="fadeUp">
              <h2 className="font-baskerville font-bold text-intercasa-dark text-2xl md:text-3xl mb-4">
                ¿Necesitas información para tu negocio?
              </h2>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.1}>
              <p className="text-center font-lato text-intercasa-dark/60 text-lg mb-8">
                Estamos aquí para ayudarte a encontrar el formato y producto perfecto para tu establecimiento.
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.2}>
              <Link to="/contacto">
                <MagneticButton variant="outline" size="md">
                  Contáctanos
                </MagneticButton>
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default HorecaProductsPageImmersive;
